import { z } from "zod";

/**
 * Two orthogonal dimensions, one per enum:
 *
 *  - `ClubStatus` (lifecycle) — WHERE the club is in its journey. Owned by
 *    onboarding (`mockup`), the mockup→trial claim CF (`trial`), and deletion
 *    / suspension (`closed`). Never touched by the Stripe webhook.
 *  - `BillingStatus` (payment) — DOES it pay, and is payment current. Owned by
 *    the Stripe webhook (`current` / `past_due`) and the admin exempt toggle
 *    (`exempt`). `not_billable` covers pre-billing lifecycle (mockup / trial).
 *
 * Why split: the old single `status` mixed `past_due` (billing) into the
 * lifecycle list and `subscriptionExempt` masked the lifecycle label in admin.
 * Keeping them separate lets each gate / display read the dimension it means.
 *
 * Owner-intent nuance still lives as orthogonal flags:
 *   - "winding down after owner cancel" → `subscription.cancelAtPeriodEnd`
 *   - "owner requested deletion"        → `clubs/{id}.deletionRequestedAt`
 *   - grace deadline after retries exhaust → `subscription.graceEndDate`
 */

// --- Lifecycle ---
export const CLUB_STATUS = [
  // Pre-claim. Admin-built mockup with seeded data; preview-token holders get
  // read-only access. No trial countdown yet — starts on owner accept.
  "mockup",
  // Free trial period. Full access. Pre-billing.
  "trial",
  // Claimed + live (trial or paying — billing health lives in BillingStatus).
  "active",
  // Terminal off-platform state. Disambiguate via `deletionRequestedAt`:
  // set → owner-requested deletion (holding page); null → unpaid suspension
  // (reactivation prompt).
  "closed",
] as const;

export const ClubStatusSchema = z.enum(CLUB_STATUS);
export type ClubStatus = z.infer<typeof ClubStatusSchema>;

export const CLUB_STATUS_LABELS: Record<ClubStatus, string> = {
  mockup: "Maqueta",
  trial: "Periodo de prueba",
  active: "Activo",
  closed: "Cerrado",
};

// --- Billing ---
export const BILLING_STATUS = [
  // Pre-billing: mockup or trial. No Stripe subscription charging yet.
  "not_billable",
  // Paying, subscription current.
  "current",
  // Payment isn't current — Stripe retrying OR in the grace window. Read-only.
  // Use `subscription.graceEndDate` for the deadline, not the status.
  "past_due",
  // Platform-admin escape hatch: never charged, always full access. Folds in
  // the former `subscriptionExempt` flag.
  "exempt",
] as const;

export const BillingStatusSchema = z.enum(BILLING_STATUS);
export type BillingStatus = z.infer<typeof BillingStatusSchema>;

export const BILLING_STATUS_LABELS: Record<BillingStatus, string> = {
  not_billable: "Sin facturar",
  current: "Al día",
  past_due: "Pago pendiente",
  exempt: "Exento",
};

// --- Access derivation (single arbiter) ---
export type ClubAccessLevel = "full" | "read_only" | "blocked";

/**
 * The single source of truth for what a club can do, from the two dimensions
 * + the deletion flag. Order matters: blocked beats read-only beats full.
 *   - blocked   → `closed` lifecycle, or a pending deletion.
 *   - read_only → `past_due` billing (exempt never blocks).
 *   - full      → everything else (mockup/trial/active × not_billable/current/exempt).
 */
export function clubAccessLevel(
  status: ClubStatus,
  billingStatus: BillingStatus,
  deletionRequestedAt?: Date | null,
): ClubAccessLevel {
  if (status === "closed" || deletionRequestedAt != null) return "blocked";
  if (billingStatus === "past_due") return "read_only";
  return "full";
}

/** App-shell warning banner: only for an overdue, non-exempt club. */
export function shouldShowPaymentWarning(billingStatus: BillingStatus): boolean {
  return billingStatus === "past_due";
}

/**
 * Legal lifecycle transitions. Billing transitions are independent — Stripe
 * moves BillingStatus on its own axis — EXCEPT the two "billing settled"
 * events that also end the trial (see `exemptLifecycleEffect` + the webhook).
 *   mockup → trial   (owner accepts the invite from a preview claim)
 *   mockup → closed  (admin abandons the prospect)
 *   trial  → active  (billing settled: owner's first cleared payment, OR an
 *                     admin grants Exento — the no-charge equivalent)
 *   trial  → closed  (abandoned)
 *   active → closed  (cancellation finalised / suspended)
 *   closed → active  (owner reactivates)
 */
const CLUB_STATUS_TRANSITIONS: Record<ClubStatus, readonly ClubStatus[]> = {
  mockup: ["trial", "closed"],
  trial: ["active", "closed"],
  active: ["closed"],
  closed: ["active"],
};

export function canTransitionClubStatus(
  from: ClubStatus,
  to: ClubStatus,
): boolean {
  if (from === to) return true;
  return CLUB_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Lifecycle effect of toggling billing-exempt. Granting Exento is the
 * no-charge equivalent of a first cleared payment, so it advances the trial:
 * `trial → active` — mirroring the Stripe `invoice.paid` promotion in
 * functions `_apply_billing_to_lifecycle`. Without this, an exempted club
 * keeps a stale `trial` status and the admin row reads the incoherent
 * "Prueba caducada" (a failed prospect) on a club we deliberately comped.
 *
 * Scoped deliberately:
 *   - Only `trial` is promoted. `mockup` (unclaimed prospect) and `closed`
 *     (terminal) are left untouched — exempting them must not claim/resurrect.
 *   - Un-exempting (`granting === false`) NEVER demotes: an active club that
 *     loses its comp simply has to pay again (billing falls to not_billable).
 *
 * Returns the lifecycle status to write, or `null` to leave `status` untouched.
 * Pure — callers do the Firestore write.
 */
export function exemptLifecycleEffect(
  granting: boolean,
  currentStatus: ClubStatus,
): ClubStatus | null {
  return granting && currentStatus === "trial" ? "active" : null;
}
